import { useEffect, useState, useCallback } from 'react'
import { Monitor, Smartphone, LogOut, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useUser } from '../contexts/UserContext'
import { getDeviceId } from '../utils/deviceFingerprint'

interface ActiveSession {
    id: string
    device_type: 'desktop' | 'mobile'
    device_id: string
    device_info: {
        browser: string
        os: string
    }
    last_active: string
}

export function ActiveSessionsList() {
    const { user } = useUser()
    const [sessions, setSessions] = useState<ActiveSession[]>([])
    const [loading, setLoading] = useState(true)
    const currentDeviceId = getDeviceId()

    const fetchSessions = useCallback(async () => {
        if (!user) return

        const { data } = await supabase
            .from('active_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('last_active', { ascending: false })

        if (data) {
            // Cast manual para garantir compatibilidade de tipos, já que device_info vem como Json do Supabase
            const typedData: ActiveSession[] = data.map(session => ({
                ...session,
                device_info: session.device_info as unknown as { browser: string; os: string }
            }))
            setSessions(typedData)
        }
        setLoading(false)
    }, [user])

    useEffect(() => {
        // eslint-disable-next-line
        fetchSessions()
    }, [fetchSessions])

    const handleLogoutSession = async (sessionId: string) => {
        await supabase
            .from('active_sessions')
            .delete()
            .eq('id', sessionId)

        fetchSessions()
    }

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
                <Monitor className="w-5 h-5 text-blue-600" />
                <h3 className="text-md font-semibold text-gray-900">Dispositivos Ativos</h3>
            </div>

            <div className="space-y-3">
                {sessions.map(session => {
                    const isCurrent = session.device_id === currentDeviceId
                    const lastActive = new Date(session.last_active).toLocaleString('pt-BR')

                    return (
                        <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-gray-200 text-gray-500">
                                    {session.device_type === 'mobile' ? <Smartphone className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 flex items-center gap-2">
                                        {session.device_info.browser} no {session.device_info.os}
                                        {isCurrent && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Este dispositivo</span>}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Ativo em: {lastActive}
                                    </div>
                                </div>
                            </div>

                            {!isCurrent && (
                                <button
                                    onClick={() => handleLogoutSession(session.id)}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Desconectar"
                                >
                                    <LogOut className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    )
                })}

                {sessions.length === 0 && (
                    <p className="text-gray-500 text-center py-4">Nenhum dispositivo ativo encontrado.</p>
                )}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800">
                    <strong>Nota de Segurança:</strong> Você pode ter no máximo 1 Desktop e 1 Mobile conectados simultaneamente.
                </p>
            </div>
        </div>
    )
}
